import { useLocation, useSearchParams } from 'react-router-dom';
import PayerConnections from '@features/admin-v2/views/payer-connections';
import { useUserRoles } from '@features/user';
import { useDebounce } from '@hooks/useDebounce';
import { Alert } from '@mui/material';
import { FeatureGates, useFeatureGate } from '@services/statsig';

import { type FleetTrack, StaffIntakeFleet } from './components/StaffIntakeFleet';
import { STAFF_INTAKE_TABS, StaffIntakeStatusShell } from './components/StaffIntakeStatusShell';
import { useGetAdminIntakeFleetQuery } from './store/api';
import { AdminIntakeVerificationQueue } from './AdminIntakeVerificationQueue';

function FleetContent({ track, canVerify }: { track: FleetTrack; canVerify: boolean }) {
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const prefix =
    track === 'overview' ? 'overview' : track === 'credentialing' ? 'credentialing' : 'ehr';
  const search = (params.get(`${prefix}Search`) ?? '').slice(0, 100);
  const debounced = useDebounce(search, 300);
  const requestedPage = Number(params.get(`${prefix}Page`) ?? 1);
  const page = Number.isSafeInteger(requestedPage) && requestedPage > 0 ? requestedPage : 1;
  const query = useGetAdminIntakeFleetQuery(
    { search: debounced, page, perPage: 20 },
    { refetchOnMountOrArgChange: true, refetchOnFocus: true, refetchOnReconnect: true },
  );
  const update = (key: string, value: string) =>
    setParams(
      previous => {
        const next = new URLSearchParams(previous);
        next.set(`${prefix}${key}`, value);
        if (key === 'Search') next.set(`${prefix}Page`, '1');
        return next;
      },
      { replace: true },
    );
  return (
    <StaffIntakeFleet
      track={track}
      canVerify={canVerify}
      data={search === debounced ? query.currentData : undefined}
      loading={query.isFetching || search !== debounced}
      failed={query.isError}
      search={search}
      page={page}
      perPage={20}
      returnTo={location.pathname + location.search}
      onSearch={value => update('Search', value)}
      onPage={value => update('Page', String(value))}
      onRefresh={() => void query.refetch()}
    />
  );
}

export function StaffIntakeStatusPage() {
  const roles = useUserRoles();
  const admin = roles.includes('Admin');
  const authorized = admin || roles.includes('Moderator');
  const payerEnabled = useFeatureGate(FeatureGates.PAYER_CONNECTIONS);
  const [params, setParams] = useSearchParams();
  const selected =
    STAFF_INTAKE_TABS.find(tab => tab.value === params.get('statusTab'))?.value ?? 'overview';
  if (!authorized) return <Alert severity="error">Staff access is required.</Alert>;
  return (
    <StaffIntakeStatusShell
      selected={selected}
      onSelect={value =>
        setParams(previous => {
          const next = new URLSearchParams(previous);
          next.set('statusTab', value);
          return next;
        })
      }
    >
      {selected === 'verification' ? (
        admin ? (
          <AdminIntakeVerificationQueue />
        ) : (
          <Alert severity="info">
            Verification review requires Admin access. Billing and recorded credentialing data
            remain available.
          </Alert>
        )
      ) : selected === 'billing' ? (
        payerEnabled ? (
          <PayerConnections embedded />
        ) : (
          <Alert severity="info">
            Payer Connections is not enabled for your staff account. Existing organization tools
            remain available.
          </Alert>
        )
      ) : (
        <FleetContent key={selected} track={selected} canVerify={admin} />
      )}
    </StaffIntakeStatusShell>
  );
}
